import { BasicProcessPhase, ConditionStatus } from "../../utils/enums";
import { Param, ParamUtils } from "./Param";

// TransferMetadata interface
export interface ITransferMetadata {
  readonly transfer_status: string;
  readonly transfer_id: string;
  readonly transfer_data: string;
  readonly transfer_date_time: Date;
  readonly signatureData?: ISignatureData;
  readonly method?: string;
}

export const ITransferMetadataUtils = {
  create({
    transfer_status,
    transfer_id,
    transfer_data,
    transfer_date_time,
    signatureData,
    method,
  }: {
    transfer_status: string;
    transfer_id: string;
    transfer_data: string;
    transfer_date_time: Date;
    signatureData?: ISignatureData;
    method?: string;
  }): ITransferMetadata {
    return {
      transfer_status,
      transfer_id,
      transfer_data,
      transfer_date_time,
      signatureData,
      method,
    };
  },

  empty(): ITransferMetadata {
    return {
      transfer_status: "",
      transfer_id: "",
      transfer_data: "",
      transfer_date_time: new Date(),
    };
  },

  props(metadata: ITransferMetadata): any[] {
    return [
      metadata.transfer_status,
      metadata.transfer_id,
      metadata.transfer_data,
      metadata.transfer_date_time,
      metadata.signatureData,
      metadata.method,
    ];
  },

  copyWith(metadata: ITransferMetadata, updates: Partial<ITransferMetadata>): ITransferMetadata {
    return {
      ...metadata,
      ...updates,
    };
  },

  toMap(metadata: ITransferMetadata): Record<string, any>[] {
    const formatDate = (date: Date, format: string): string => {
      if (format === "MM/dd/yyyy") {
        return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
          .getDate()
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`;
      }
      if (format === "hh:mm:ss aa") {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const ampm = hours >= 12 ? "PM" : "AM";
        const hour12 = hours % 12 || 12;
        return `${hour12.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")} ${ampm}`;
      }
      return date.toString();
    };

    return [
      { transfer_status: metadata.transfer_status },
      ParamUtils.toMap(ParamUtils.create({ key: "transfer_id", value: metadata.transfer_id })),
      ParamUtils.toMap(
        ParamUtils.create({
          key: "transfer_date",
          value: formatDate(metadata.transfer_date_time, "MM/dd/yyyy"),
        })
      ),
      ParamUtils.toMap(
        ParamUtils.create({
          key: "transfer_data",
          value: metadata.transfer_data,
        })
      ),
      ParamUtils.toMap(
        ParamUtils.create({
          key: "transfer_time",
          value: formatDate(metadata.transfer_date_time, "hh:mm:ss aa"),
        })
      ),
      { method: metadata.method },
    ];
  },

  _valueFromParamsByKey(params: Param[], key: string): string {
    const param = params.find((p) => p.key === key);
    return param ? param.value : "";
  },

  fromMap(map: Record<string, any>[]): ITransferMetadata {
    try {
      const params = map.filter((d) => d["key"] != null).map((e) => ParamUtils.fromMap(e));
      const transfer_date = ITransferMetadataUtils._valueFromParamsByKey(params, "transfer_date");
      const transfer_time = ITransferMetadataUtils._valueFromParamsByKey(params, "transfer_time");
      const transfer_data = ITransferMetadataUtils._valueFromParamsByKey(params, "transfer_data");

      let sig: Record<string, string> | undefined;
      if (transfer_data === "") {
        const emptyPayload = { key: "signed_payload", value: {} };
        const keys = ["transfer_data", "signed_payload"];
        const value = (map.find((p) => keys.includes(p["key"]) && typeof p["value"] !== "string") ||
          emptyPayload)["value"];
        sig = value["signedMessage"] instanceof Array ? value["signedMessage"][0] : value;
      }

      const status = map.find((d) => d["transfer_status"])?.["transfer_status"] ?? "";
      const method = map.find((d) => d["method"])?.["method"] ?? "";

      return {
        transfer_status: status,
        transfer_id: ITransferMetadataUtils._valueFromParamsByKey(params, "transfer_id"),
        transfer_data: transfer_data,
        transfer_date_time: new Date(`${transfer_date} ${transfer_time}`),
        signatureData: sig ? ISignatureDataUtils.fromMap(sig) : undefined,
        method: method,
      };
    } catch (e) {
      console.error(e);
      return ITransferMetadataUtils.empty();
    }
  },

  toJson(metadata: ITransferMetadata): string {
    return JSON.stringify(ITransferMetadataUtils.toMap(metadata));
  },

  fromJson(source: string): ITransferMetadata {
    return ITransferMetadataUtils.fromMap(JSON.parse(source));
  },

  toString(metadata: ITransferMetadata): string {
    return JSON.stringify(ITransferMetadataUtils.props(metadata));
  },
};

// SignatureData interface
export interface ISignatureData {
  readonly r: string;
  readonly s: string;
  readonly v: string;
  readonly m: string;
  readonly sig: string;
  readonly metadata?: string;
}

export const ISignatureDataUtils = {
  create(
    r: string,
    s: string,
    v: string,
    m: string,
    sig: string,
    metadata?: string
  ): ISignatureData {
    return { r, s, v, m, sig, metadata };
  },

  empty(): ISignatureData {
    return { r: "", s: "", v: "", m: "", sig: "" };
  },

  fromMap(json: Record<string, string> | null | undefined): ISignatureData {
    if (!json || !json["r"] || json["r"] === "") return ISignatureDataUtils.empty();
    return {
      r: json["r"]!,
      s: json["s"]!,
      v: json["v"]!,
      m: json["hash"]!,
      sig: json["sig"]!,
      metadata: json["metadata"],
    };
  },

  toEthSignature(signatureData: ISignatureData): string {
    try {
      return `0x${signatureData.r}${signatureData.s}${(parseInt(signatureData.v) + 27).toString(16)}`;
    } catch (e) {
      return "";
    }
  },
};

// CurrentPhase interface
export interface ICurrentPhase {
  readonly index: BasicProcessPhase;
  readonly name: string;
  readonly description: string;
  readonly data: any[];
  readonly errors: ISafeObjectErrorLog[];
}

export const ICurrentPhaseUtils = {
  create({
    index,
    name,
    description,
    data,
    errors,
  }: {
    index: BasicProcessPhase;
    name: string;
    description: string;
    data: any[];
    errors: ISafeObjectErrorLog[];
  }): ICurrentPhase {
    return {
      index,
      name,
      description,
      data,
      errors,
    };
  },

  empty(): ICurrentPhase {
    return {
      index: BasicProcessPhase.NON_EXISTS,
      name: "",
      description: "",
      data: [],
      errors: [],
    };
  },

  fromMap(map: Record<string, any>): ICurrentPhase {
    try {
      const arr: any[] = [];
      const data = map["data"];
      if (data && data !== "") {
        for (const item of Array.isArray(data) ? data : [data]) {
          if (typeof item === "string") {
            continue;
          }

          if (Array.isArray(item)) {
            const hasTransferStatus = item.some(
              (d: any) => typeof d !== "string" && d["transfer_status"] != null
            );
            if (hasTransferStatus) {
              arr.push(ITransferMetadataUtils.fromMap(item as Record<string, any>[]));
            } else {
              for (const subItem of item) {
                if (typeof subItem === "string") continue;
                if (subItem["user"] != null) {
                  arr.push(IUserVoteMetadataUtils.fromMap(subItem));
                } else if (subItem["members"] != null) {
                  arr.push(IVoteMetadataUtils.fromMap(subItem));
                } else if (subItem["policyID"] != null) {
                  arr.push(IPolicyMetaDataUtils.fromMap(subItem));
                }
              }
            }
          } else if (typeof item === "object" && item != null) {
            console.log("i is not List");
          }
        }
      }

      const indexValue = parseInt(String(map["index"]));
      const validIndex = !isNaN(indexValue) ? Math.min(Math.max(indexValue, 0), 5) : 0;
      const phaseValues = Object.values(BasicProcessPhase);

      return {
        index: phaseValues[validIndex] as BasicProcessPhase,
        name: String(map["name"] ?? ""),
        description: String(map["description"] ?? ""),
        data: arr,
        errors:
          Array.isArray(map["errors"]) && map["errors"].length > 0
            ? map["errors"].map((error: any) => ISafeObjectErrorLogUtils.fromMap(error))
            : [],
      };
    } catch (e) {
      console.error(e);
      return ICurrentPhaseUtils.empty();
    }
  },

  toMap(phase: ICurrentPhase): Record<string, any> {
    return {
      index: phase.index,
      name: phase.name,
      description: phase.description,
    };
  },

  toJson(phase: ICurrentPhase): string {
    return JSON.stringify(ICurrentPhaseUtils.toMap(phase));
  },
};

// SafeObjectErrorLog interface
export interface ISafeObjectErrorLog {
  message: string;
  conditionName: string;
  conditionClass: string;
  conditionId: string;
}

export const ISafeObjectErrorLogUtils = {
  create({
    message,
    conditionName,
    conditionClass,
    conditionId,
  }: {
    message: string;
    conditionName: string;
    conditionClass: string;
    conditionId: string;
  }): ISafeObjectErrorLog {
    return {
      message,
      conditionName,
      conditionClass,
      conditionId,
    };
  },

  empty(): ISafeObjectErrorLog {
    return {
      conditionClass: "",
      conditionId: "",
      conditionName: "",
      message: "",
    };
  },

  copyWith(
    errorLog: ISafeObjectErrorLog,
    {
      message,
      conditionName,
      conditionClass,
      conditionId,
    }: {
      message?: string;
      conditionName?: string;
      conditionClass?: string;
      conditionId?: string;
    }
  ): ISafeObjectErrorLog {
    return {
      message: message ?? errorLog.message,
      conditionName: conditionName ?? errorLog.conditionName,
      conditionClass: conditionClass ?? errorLog.conditionClass,
      conditionId: conditionId ?? errorLog.conditionId,
    };
  },

  toMap(errorLog: ISafeObjectErrorLog): Record<string, any> {
    return {
      message: errorLog.message,
      conditionName: errorLog.conditionName,
      conditionClass: errorLog.conditionClass,
      conditionId: errorLog.conditionId,
    };
  },

  fromMap(map: Record<string, any>): ISafeObjectErrorLog {
    return {
      message: map["message"] ?? "",
      conditionName: map["condition_name"] ?? "",
      conditionClass: map["condition_class"] ?? "",
      conditionId: map["condition_id"] ?? "",
    };
  },

  toJson(errorLog: ISafeObjectErrorLog): string {
    return JSON.stringify(ISafeObjectErrorLogUtils.toMap(errorLog));
  },

  fromJson(source: string): ISafeObjectErrorLog {
    return ISafeObjectErrorLogUtils.fromMap(JSON.parse(source));
  },

  toString(errorLog: ISafeObjectErrorLog): string {
    return `SafeObjectErrorLog(message: ${errorLog.message}, conditionName: ${errorLog.conditionName}, conditionClass: ${errorLog.conditionClass}, conditionId: ${errorLog.conditionId})`;
  },

  equals(errorLog1: ISafeObjectErrorLog, errorLog2: ISafeObjectErrorLog): boolean {
    return (
      errorLog1.message === errorLog2.message &&
      errorLog1.conditionName === errorLog2.conditionName &&
      errorLog1.conditionClass === errorLog2.conditionClass &&
      errorLog1.conditionId === errorLog2.conditionId
    );
  },
};

// PolicyMetaData interface
export interface IPolicyMetaData {
  readonly policyID: string;
  readonly conditionStatus: ConditionStatus;
  readonly conditionClass: string;
}

export const IPolicyMetaDataUtils = {
  create(
    policyID: string,
    conditionStatus: ConditionStatus,
    conditionClass: string
  ): IPolicyMetaData {
    return {
      policyID,
      conditionStatus,
      conditionClass,
    };
  },

  empty(): IPolicyMetaData {
    return {
      policyID: "",
      conditionStatus: ConditionStatus.NON_RELEVANT,
      conditionClass: "",
    };
  },

  fromMap(map: Record<string, any>): IPolicyMetaData {
    try {
      const conditionStatusIndex =
        map["conditionStatus"] != null && !isNaN(parseInt(map["conditionStatus"]))
          ? parseInt(map["conditionStatus"])
          : 2;
      const conditionStatusValues = Object.values(ConditionStatus);
      const conditionStatus =
        conditionStatusValues[conditionStatusIndex] || ConditionStatus.NON_RELEVANT;

      return {
        policyID: (map["policyID"] ?? "").toString(),
        conditionStatus,
        conditionClass: (map["conditionClass"] ?? "").toString(),
      };
    } catch (e) {
      console.error(e);
      return IPolicyMetaDataUtils.empty();
    }
  },

  toMap(policyData: IPolicyMetaData): Record<string, any> {
    return {
      policyID: policyData.policyID,
      conditionStatus: policyData.conditionStatus,
      conditionClass: policyData.conditionClass,
    };
  },

  toJson(policyData: IPolicyMetaData): string {
    return JSON.stringify(IPolicyMetaDataUtils.toMap(policyData));
  },
};

// UserVoteMetadata interface
export interface IUserVoteMetadata {
  user: string;
  vote: string;
  voteSIGNATURE: string;
}

export const IUserVoteMetadataUtils = {
  create({
    user,
    vote,
    voteSIGNATURE,
  }: {
    user: string;
    vote: string;
    voteSIGNATURE: string;
  }): IUserVoteMetadata {
    return {
      user,
      vote,
      voteSIGNATURE,
    };
  },

  empty(): IUserVoteMetadata {
    return { user: "", vote: "", voteSIGNATURE: "" };
  },

  fromMap(map: Record<string, any>): IUserVoteMetadata {
    try {
      const tempFix = (map["user"] ?? "[]").toString().replaceAll("[", "").replaceAll("]", "");
      return {
        user: tempFix,
        vote: (map["vote"] ?? "").toString(),
        voteSIGNATURE: (map["voteSIGNATURE"] ?? "").toString(),
      };
    } catch (e) {
      console.error(e);
      return IUserVoteMetadataUtils.empty();
    }
  },

  toMap(userVote: IUserVoteMetadata): Record<string, any> {
    return {
      user: userVote.user.toString(),
      vote: userVote.vote,
      voteSIGNATURE: userVote.voteSIGNATURE,
    };
  },

  toJson(userVote: IUserVoteMetadata): string {
    return JSON.stringify(IUserVoteMetadataUtils.toMap(userVote));
  },

  copyWith(
    userVote: IUserVoteMetadata,
    {
      user,
      vote,
      voteSIGNATURE,
    }: {
      user?: string;
      vote?: string;
      voteSIGNATURE?: string;
    }
  ): IUserVoteMetadata {
    return {
      user: user ?? userVote.user,
      vote: vote ?? userVote.vote,
      voteSIGNATURE: voteSIGNATURE ?? userVote.voteSIGNATURE,
    };
  },

  copyWithFromLocalVote(
    userVote: IUserVoteMetadata,
    { localVote }: { localVote: ILocalVote }
  ): IUserVoteMetadata {
    return {
      user: localVote.user,
      vote: localVote.vote,
      voteSIGNATURE: localVote.voteSIGNATURE,
    };
  },
};

// VoteMetadata interface
export interface IVoteMetadata {
  group: string;
  members: number;
  yes: number;
  no: number;
}

export const IVoteMetadataUtils = {
  create({
    group,
    members,
    yes,
    no,
  }: {
    group: string;
    members: number;
    yes: number;
    no: number;
  }): IVoteMetadata {
    return {
      group,
      members,
      yes,
      no,
    };
  },

  empty(): IVoteMetadata {
    return { group: "", members: 0, yes: 0, no: 0 };
  },

  fromMap(map: Record<string, any>): IVoteMetadata {
    return {
      group: map["group"] as string,
      members: Number.isNaN(parseInt(map["members"])) ? 0 : parseInt(map["members"].toString()),
      yes: Number.isNaN(parseInt(map["yes"])) ? 0 : parseInt(map["yes"].toString()),
      no: Number.isNaN(parseInt(map["no"])) ? 0 : parseInt(map["no"].toString()),
    };
  },

  toMap(voteMetadata: IVoteMetadata): Record<string, any> {
    return {
      group: voteMetadata.group,
      members: voteMetadata.members,
      yes: voteMetadata.yes.toString(),
      no: voteMetadata.no.toString(),
    };
  },

  toJson(voteMetadata: IVoteMetadata): string {
    return JSON.stringify(IVoteMetadataUtils.toMap(voteMetadata));
  },

  copyWith(
    voteMetadata: IVoteMetadata,
    {
      group,
      members,
      yes,
      no,
    }: {
      group?: string;
      members?: number;
      yes?: number;
      no?: number;
    }
  ): IVoteMetadata {
    return {
      group: group ?? voteMetadata.group,
      members: members ?? voteMetadata.members,
      yes: yes ?? voteMetadata.yes,
      no: no ?? voteMetadata.no,
    };
  },

  toString(voteMetadata: IVoteMetadata): string {
    return `VoteMetadata(group: ${voteMetadata.group}, members: ${voteMetadata.members}, yes: ${voteMetadata.yes}, no: ${voteMetadata.no})`;
  },

  equals(vote1: IVoteMetadata, vote2: IVoteMetadata): boolean {
    return (
      vote1.group === vote2.group &&
      vote1.members === vote2.members &&
      vote1.yes === vote2.yes &&
      vote1.no === vote2.no
    );
  },
};

// LocalVote interface (extends IUserVoteMetadata)
export interface ILocalVote extends IUserVoteMetadata {
  readonly safeObjectSignature: string;
}

export const ILocalVoteUtils = {
  create(
    safeObjectSignature: string,
    vote: string,
    user: string,
    voteSIGNATURE: string
  ): ILocalVote {
    return {
      safeObjectSignature,
      vote,
      user,
      voteSIGNATURE,
    };
  },

  copyWith(
    localVote: ILocalVote,
    { safeObjectSignature, vote, user, voteSIGNATURE }: Partial<ILocalVote>
  ): ILocalVote {
    return {
      safeObjectSignature: safeObjectSignature ?? localVote.safeObjectSignature,
      vote: vote ?? localVote.vote,
      user: user ?? localVote.user,
      voteSIGNATURE: voteSIGNATURE ?? localVote.voteSIGNATURE,
    };
  },

  toMap(localVote: ILocalVote): Record<string, any> {
    const map = { ...IUserVoteMetadataUtils.toMap(localVote) };
    map["safeObjectSignature"] = localVote.safeObjectSignature;
    return map;
  },

  toJson(localVote: ILocalVote): string {
    return JSON.stringify(ILocalVoteUtils.toMap(localVote));
  },

  fromMap(map: Record<string, any>): ILocalVote {
    return {
      safeObjectSignature: map["safeObjectSignature"] || "",
      vote: map["vote"] || "",
      user: map["user"] || "",
      voteSIGNATURE: map["voteSIGNATURE"] || "",
    };
  },
};
